import * as React from 'react';
import { addMonths } from 'date-fns';
import { useTheme } from '../../../theme';
import { getAllYears, getMonthsList, getTimestamp, isMonthAvailable, SelectedTimestampDaysMap } from '../../utils';
import { Month } from './Month/Month';
import { MonthHeader } from './Month/MonthHeader/MonthHeader';
import { HighlightedTimestamps } from '../../types';
import { useMediaQuery } from 'react-responsive';
import { MOBILE_MIN_WIDTH } from '../../../utils';
import { HoverDates } from '../../../SearchForm/components/SearchForm/Segments/Segment/Datepicker/Datepicker';
import classNames from 'classnames';
import { MonthBaseDateVariant } from './types';
import { CdnIcon } from '@shared/ui';

interface Props {
	initialDate: Date;
	monthCount: number;
	monthsToScroll?: number;
	monthBaseDateVariant?: MonthBaseDateVariant;
	onSelect: (date: Date) => void;
	minDate: Date;
	maxDate: Date;
	selectedDates: SelectedTimestampDaysMap;
	highlightedDates: HighlightedTimestamps;
	getParentRef?: () => React.RefObject<HTMLDivElement>;
	onHover?: (date: Date) => void;
	hoverDates?: HoverDates;
	frameless?: boolean;
	monthsHeader?: React.ReactNode;
}

export const MonthsWrapper: React.FunctionComponent<Props> = ({
	initialDate,
	monthCount,
	monthsToScroll,
	monthBaseDateVariant = 'focused',
	onSelect,
	minDate,
	maxDate,
	selectedDates,
	monthsHeader,
	highlightedDates,
	getParentRef,
	onHover,
	hoverDates,
	frameless
}) => {
	const [focusedDate, setFocusedDate] = React.useState<Date>(initialDate);
	const isMobile = useMediaQuery({ maxWidth: MOBILE_MIN_WIDTH });
	const headerRef = React.useRef<HTMLDivElement>(null);

	React.useEffect(() => {
		if (isMobile && getParentRef) {
			const parent = getParentRef().current;

			if (
				parent &&
				headerRef.current &&
				(parent.hasOwnProperty('scroll') || typeof parent.scroll === 'function')
			) {
				parent.scroll({
					behavior: 'auto',
					top: headerRef.current.offsetTop
				});
			}
		}
	}, []);

	const theme = useTheme('Datepicker').MonthsWrapperStyles,
		yearsArray = React.useMemo(() => getAllYears(getTimestamp(minDate), getTimestamp(maxDate)), [minDate, maxDate]);

	let minSelectedDatesTimestamp = Infinity,
		maxSelectedDatesTimestamp = 0;
	let minHoveredDatesTimestamp = Infinity,
		maxHoveredDatesTimestamp = 0;

	const setMonth = (date: Date) => {
		setFocusedDate(date);
	};

	const setCurrentDate = (date: number, monthOffset: number = 0) => {
		let newDate = new Date(parseInt(date.toString()));

		if (monthOffset !== 0) {
			newDate = addMonths(newDate, monthOffset);
		}

		setMonth(newDate);
	};

	for (const selectedDate in selectedDates) {
		if (selectedDates.hasOwnProperty(selectedDate)) {
			const timestamp = parseInt(selectedDate);

			if (timestamp < minSelectedDatesTimestamp) {
				minSelectedDatesTimestamp = timestamp;
			}

			if (timestamp > maxSelectedDatesTimestamp) {
				maxSelectedDatesTimestamp = timestamp;
			}
		}
	}

	for (const hoverDateIndex in hoverDates) {
		if (hoverDates[hoverDateIndex]) {
			const timestamp = hoverDates[hoverDateIndex].getTime();

			if (timestamp < minHoveredDatesTimestamp) {
				minHoveredDatesTimestamp = timestamp;
			}

			if (timestamp > maxHoveredDatesTimestamp) {
				maxHoveredDatesTimestamp = timestamp;
			}
		}
	}

	const monthsToScrollValue = monthsToScroll ?? 1;
	/**
	 * WEBSKY-8059:
	 * для мобильного разрешения, если календарь реализован как лист, который скролится
	 * выставляем базовую дату на минимальную дату, чтобы был доступ к "прошлым" датам
	 */
	const monthListBaseDate = monthBaseDateVariant === 'focused' ? focusedDate ?? minDate : minDate;

	const renderMonth = (date: Date, index: number, addRef?: boolean) => {
		return (
			<Month
				key={index}
				date={date}
				selectedDatesMap={selectedDates}
				minDateTimestamp={getTimestamp(minDate)}
				maxDateTimestamp={getTimestamp(maxDate)}
				minSelectedDateTimestamp={minSelectedDatesTimestamp}
				maxSelectedDateTimestamp={maxSelectedDatesTimestamp}
				minHoveredDateTimestamp={minHoveredDatesTimestamp}
				maxHoveredDateTimestamp={maxHoveredDatesTimestamp}
				highlightingDates={highlightedDates}
				onSelect={onSelect}
				onHover={onHover}
				monthHeader={
					<div
						ref={
							addRef && focusedDate.getMonth && focusedDate.getMonth() === date.getMonth()
								? headerRef
								: null
						}
					>
						<MonthHeader
							setCurrentMonth={setCurrentDate}
							years={yearsArray}
							monthIndex={index}
							currentDate={date}
							maxDate={maxDate}
							minDate={minDate}
						/>
					</div>
				}
			/>
		);
	};

	return (
		<div className={classNames(theme.wrapper, { [theme.frameless]: frameless })}>
			<nav>
				{isMonthAvailable(focusedDate, -1, minDate, maxDate) && (
					<div
						data-test-id="calendar-prev-month"
						className={theme.nav_prev}
						onClick={() => setMonth(addMonths(focusedDate, -monthsToScrollValue))}
					>
						<CdnIcon icon={'Arrow/Chevron_left'} />
					</div>
				)}

				{isMonthAvailable(focusedDate, monthCount, minDate, maxDate) && (
					<div
						data-test-id="calendar-next-month"
						className={theme.nav_next}
						onClick={() => setMonth(addMonths(focusedDate, monthsToScrollValue))}
					>
						<CdnIcon icon={'Arrow/Chevron_right'} />
					</div>
				)}
			</nav>

			{monthsHeader}
			<div className={theme.monthsList}>
				{getMonthsList(monthListBaseDate, monthCount).map((date, index) => renderMonth(date, index, isMobile))}
			</div>
		</div>
	);
};
